import "./viewport.js";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createRoot } from "react-dom/client";
import {
  BookOpen,
  BookMarked,
  NotebookPen,
  Users,
  Settings,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Plus,
  X,
  Check,
  Lock,
  Globe,
  LogOut,
  Menu,
  Sun,
  Moon,
  Type,
  Copy,
  Trash2,
  Pencil,
  Download,
  Shield,
  UserPlus,
  WifiOff,
  MessageCircle,
  ArrowUpRight,
  Leaf,
  LoaderCircle,
  CheckCheck,
  RefreshCw,
} from "lucide-react";
import { api, storage } from "./api";
import "./styles.css";
const COLORS = ["sage", "sand", "rose", "blue", "plum"];
const fmt = (d) =>
  new Intl.DateTimeFormat("zh-CN", {
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(d));
const chapterList = (books) =>
  books.flatMap((b) =>
    Array.from(
      { length: b.bookChapterMaxNumber },
      (_, i) => `${b.bookUsfm}.${i + 1}`,
    ),
  );
function Avatar({ name = "读", color = "sage", small = false }) {
  return (
    <span className={`avatar ${color} ${small ? "small" : ""}`}>
      {name.slice(0, 1)}
    </span>
  );
}
function Empty({ icon: Icon = Leaf, title, children }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon size={28} strokeWidth={1.35} />
      </div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
function Modal({ title, onClose, children, wide = false }) {
  const ref = useRef();
  useEffect(() => {
    const before = document.activeElement;
    const el = ref.current;
    const targets = () =>
      [
        ...el.querySelectorAll(
          'button,input,textarea,select,a[href],[tabindex="0"]',
        ),
      ].filter((e) => !e.disabled);
    targets()[0]?.focus();
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const all = targets(),
          first = all[0],
          last = all.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    el.addEventListener("keydown", key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      el.removeEventListener("keydown", key);
      document.body.style.overflow = prev;
      before?.focus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal ${wide ? "wide" : ""}`}
      >
        <header>
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function PasswordForm({ onDone, first = false }) {
  const [oldPassword, setOld] = useState(""),
    [newPassword, setNew] = useState(""),
    [again, setAgain] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        if (newPassword !== again) return setError("两次输入的新密码不一致");
        setBusy(true);
        setError("");
        try {
          onDone(await api("/password", "POST", { oldPassword, newPassword }));
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      {first && (
        <p className="muted">
          这是你的第一次登录。设置一个自己的密码，开启一起阅读的旅程。
        </p>
      )}
      <label>
        原密码
        <input
          type="password"
          autoComplete="current-password"
          required
          value={oldPassword}
          onChange={(e) => setOld(e.target.value)}
        />
      </label>
      <label>
        新密码
        <input
          type="password"
          autoComplete="new-password"
          minLength={8}
          maxLength={128}
          placeholder="至少 8 个字符"
          required
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
        />
      </label>
      <label>
        确认新密码
        <input
          type="password"
          autoComplete="new-password"
          required
          value={again}
          onChange={(e) => setAgain(e.target.value)}
        />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="primary" disabled={busy}>
        {busy ? "保存中…" : first ? "开始阅读" : "更新密码"}
        <ArrowRight size={17} />
      </button>
    </form>
  );
}
function Login({ onLogin, onOffline, online }) {
  const [username, setName] = useState(""),
    [pw, setPw] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <div className="login-page">
      <div className="login-story">
        <div className="brand">
          <span className="brand-mark">
            <BookOpen size={27} />
          </span>
          <div>
            和你一起阅读<small>READ WITH YOU</small>
          </div>
        </div>
        <div className="story-content">
          <span className="eyebrow">每一页，都有相遇的可能</span>
          <h1>
            一起读，
            <br />
            让心更靠近。
          </h1>
          <p>
            在经文中安静片刻，记录此刻的感悟。
            <br />
            也听听朋友们，读到了怎样的光。
          </p>
          <div className="book-art">
            <span className="sun-disc" />
            <BookOpen size={190} strokeWidth={0.7} />
            <span className="art-line" />
          </div>
          <span className="story-caption">READ. REFLECT. CONNECT.</span>
        </div>
        <p className="login-foot">一本书，一段同行的旅程。</p>
      </div>
      <div className="login-form-wrap">
        <div className="login-form">
          <span className="eyebrow">欢迎回来</span>
          <h2>今天，也一起阅读。</h2>
          <p className="muted">使用管理员为你创建的账号登录</p>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError("");
              try {
                onLogin(await api("/login", "POST", { username, pw }));
              } catch (e) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              用户名
              <input
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                placeholder="输入用户名"
                value={username}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
            <label>
              密码
              <input
                type="password"
                autoComplete="current-password"
                placeholder="输入密码"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
            </label>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button className="primary" disabled={busy}>
              {busy ? (
                <LoaderCircle className="spin" size={18} />
              ) : (
                <>
                  登录，开始阅读
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
          <p className="login-help">
            <Lock size={14} /> 没有账号或忘记密码？请联系管理员
          </p>
          {!online && (
            <button className="secondary full" onClick={onOffline}>
              <WifiOff size={17} />
              阅读已下载的经文
            </button>
          )}
          <div className="login-edition">
            <BookMarked size={18} />
            <span>
              本期共读<strong>圣经 · 新标点和合本</strong>
            </span>
            <span className="edition-tag">简体中文</span>
          </div>
        </div>
      </div>
    </div>
  );
}
function App() {
  const [user, setUser] = useState(null),
    [checking, setChecking] = useState(true),
    [offlineGuest, setOfflineGuest] = useState(false);
  const [online, setOnline] = useState(navigator.onLine),
    [view, setView] = useState("read"),
    [books, setBooks] = useState([]),
    [chapterId, setChapterId] = useState("GEN.1"),
    [chapter, setChapter] = useState(null),
    [chapterError, setChapterError] = useState(""),
    [selected, setSelected] = useState(null),
    [editing, setEditing] = useState(null);
  const [chapterNotes, setChapterNotes] = useState([]),
    [notesError, setNotesError] = useState(""),
    [refresh, setRefresh] = useState(0),
    [modal, setModal] = useState(null),
    [sidebar, setSidebar] = useState(false),
    [toast, setToast] = useState(""),
    [settings, setSettings] = useState(() =>
      storage.get("rwy-settings", { size: 22, line: 2, theme: "light" }),
    ),
    [confirm, setConfirm] = useState(null),
    [installPrompt, setInstallPrompt] = useState(null);
  const targetVerse = useRef(null),
    chapterRef = useRef(null);
  const book = books.find((b) => b.bookUsfm === chapterId.split(".")[0]);
  const notify = useCallback((message) => setToast(message), []);
  const ask = (title, body) =>
    new Promise((resolve) => setConfirm({ title, body, resolve }));
  const nameOf = useCallback(
    (id) => {
      const [b, c, v] = id.split(".");
      return `${books.find((x) => x.bookUsfm === b)?.bookName || b} ${c}${v ? ":" + v : ""}`;
    },
    [books],
  );
  useEffect(() => {
    api("/me")
      .then(setUser)
      .catch((e) => {
        if (e.offline) setOfflineGuest(true);
      })
      .finally(() => setChecking(false));
    fetch("/bible/books.json")
      .then((r) => {
        if (!r.ok) throw Error();
        return r.json();
      })
      .then(setBooks)
      .catch(() => notify("目录未下载，请联网后重新打开"));
    const off = () => setOnline(false),
      on = () => {
        setOnline(true);
        setRefresh((n) => n + 1);
      };
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    const install = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", install);
    if ("serviceWorker" in navigator)
      navigator.serviceWorker
        .register("/sw.js")
        .catch(() => notify("离线缓存未启用，请检查浏览器设置"));
    return () => {
      window.removeEventListener("offline", off);
      window.removeEventListener("online", on);
      window.removeEventListener("beforeinstallprompt", install);
    };
  }, []);
  useEffect(() => {
    if (offlineGuest && online && !user)
      api("/me")
        .then((u) => {
          setUser(u);
          setOfflineGuest(false);
        })
        .catch((e) => {
          if (e.status === 401) setOfflineGuest(false);
        });
  }, [offlineGuest, online]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    document.documentElement.dataset.theme = settings.theme;
    storage.set("rwy-settings", settings);
  }, [settings]);
  useEffect(() => {
    if (!user || user.mustChange) return;
    const local = storage.get(`rwy-progress-${user.id}`, null);
    if (local) setChapterId(local);
    api("/progress")
      .then((p) => {
        if (p.chapter_id) {
          setChapterId(p.chapter_id);
          targetVerse.current = p.verse_id;
        }
      })
      .catch(() => {});
  }, [user?.id, user?.mustChange]);
  useEffect(() => {
    if (!user && !offlineGuest) return;
    let cancelled = false;
    setChapterError("");
    setChapter(null);
    setSelected(null);
    setEditing(null);
    fetch(`/bible/chapters/${chapterId}.json`)
      .then((r) => {
        if (!r.ok) throw Error();
        return r.json();
      })
      .then((c) => {
        if (cancelled) return;
        setChapter(c);
        if (user) {
          storage.set(`rwy-progress-${user.id}`, c.id);
          api("/progress", "PUT", {
            chapterId: c.id,
            verseId: targetVerse.current || null,
          }).catch(() => {});
        }
        if (targetVerse.current) {
          const id = targetVerse.current;
          targetVerse.current = null;
          const v = c.verses.find((v) =>
            [...v.verseIds, ...v.footnoteVerseIds].includes(id),
          );
          if (v) setSelected(v);
          setTimeout(
            () =>
              document
                .getElementById(v?.id)
                ?.scrollIntoView({ block: "center", behavior: "smooth" }),
            80,
          );
        } else window.scrollTo({ top: 0 });
      })
      .catch(() => {
        if (!cancelled)
          setChapterError(
            "这一章还没有下载。请联网后打开，或选择已缓存的章节。",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [chapterId, user?.id, offlineGuest]);
  useEffect(() => {
    if (!user || user.mustChange) {
      setChapterNotes([]);
      return;
    }
    let live = true;
    setNotesError("");
    setChapterNotes([]);
    api(`/notes?chapter=${chapterId}`)
      .then((d) => {
        if (live) setChapterNotes(d.notes);
      })
      .catch((e) => {
        if (live) {
          setNotesError(e.message);
          if (e.status === 401) {
            setUser(null);
            setOfflineGuest(false);
          }
        }
      });
    return () => {
      live = false;
    };
  }, [chapterId, user?.id, user?.mustChange, refresh, online]);
  const navigate = (next) => {
    setView(next);
    setSidebar(false);
    setSelected(null);
    setEditing(null);
    window.scrollTo({ top: 0 });
  };
  const goTo = (id, verse = null) => {
    setView("read");
    setModal(null);
    setEditing(null);
    if (id === chapterId) {
      if (verse) {
        setSelected(
          chapter?.verses.find((v) =>
            [...v.verseIds, ...v.footnoteVerseIds].includes(verse),
          ),
        );
        setTimeout(
          () =>
            document.getElementById(verse)?.scrollIntoView({ block: "center" }),
          50,
        );
      } else setSelected(null);
    } else {
      targetVerse.current = verse;
      setChapterId(id);
    }
  };
  const logout = async () => {
    if (!online) return notify("请联网后退出，以便安全结束登录会话");
    try {
      await api("/logout", "POST");
      for (const k of Object.keys(localStorage))
        if (k.startsWith("rwy-draft-") || k.startsWith("rwy-progress-"))
          localStorage.removeItem(k);
      setUser(null);
      setOfflineGuest(false);
      setChapterNotes([]);
      setSelected(null);
      setModal(null);
      setView("read");
    } catch (e) {
      notify(e.message);
    }
  };
  const editNote = (n) => {
    setEditing(n);
    setModal("edit-note");
  };
  const changed = () => {
    setRefresh((n) => n + 1);
    setEditing(null);
  };
  const deleteNote = async (n) => {
    if (!(await ask("删除这条备注？", "删除后无法恢复。"))) return;
    try {
      await api(`/notes/${n.id}`, "DELETE");
      changed();
      notify("备注已删除");
    } catch (e) {
      notify(e.message);
    }
  };
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("已复制");
    } catch {
      notify("复制失败，请检查浏览器的剪贴板权限");
    }
  };
  if (checking)
    return (
      <div className="loading-screen">
        <BookOpen size={38} />
        <p>翻开今天的阅读…</p>
      </div>
    );
  if (!user && !offlineGuest)
    return (
      <>
        <Login
          onLogin={setUser}
          onOffline={() => setOfflineGuest(true)}
          online={online}
        />
        {toast && (
          <div className="toast" role="status">
            {toast}
          </div>
        )}
      </>
    );
  if (user?.mustChange)
    return (
      <div className="first-login">
        <div className="first-card">
          <BookOpen size={32} />
          <h1>让账号只属于你</h1>
          <PasswordForm first onDone={setUser} />
          <button className="text-btn full" onClick={logout}>
            退出登录
          </button>
        </div>
      </div>
    );
  const chapters = chapterList(books),
    idx = chapters.indexOf(chapterId);
  const nav = [
    ["read", BookOpen, "阅读"],
    ["notes", NotebookPen, "我的备注"],
    ["friends", Users, "好友"],
    ["settings", Settings, "我的设置"],
  ];
  return (
    <div className={`app ${sidebar ? "sidebar-open" : ""}`}>
      {sidebar && (
        <button
          className="sidebar-scrim"
          aria-label="关闭导航"
          onClick={() => setSidebar(false)}
        />
      )}
      <aside className="sidebar">
        <a
          className="brand"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate("read");
          }}
        >
          <span className="brand-mark">
            <BookOpen size={25} />
          </span>
          <div>
            和你一起阅读<small>READ WITH YOU</small>
          </div>
        </a>
        <div className="sidebar-label">属于我们的阅读时光</div>
        <nav>
          {nav.map(([key, Icon, label]) => (
            <button
              key={key}
              className={view === key ? "active" : ""}
              onClick={() => navigate(key)}
            >
              <Icon size={20} strokeWidth={1.7} />
              {label}
              {view === key && <span className="nav-dot" />}
            </button>
          ))}
        </nav>
        <div className="sidebar-book">
          <span className="eyebrow">正在阅读</span>
          <div className="mini-book">
            <BookOpen size={36} strokeWidth={1} />
            <span>
              圣经<small>新标点和合本</small>
            </span>
          </div>
          <button
            className="text-btn"
            onClick={() => {
              navigate("read");
              setModal("directory");
            }}
          >
            {nameOf(chapterId)}
            <ArrowUpRight size={15} />
          </button>
        </div>
        <div className="sidebar-bottom">
          <p>
            “你的话是我脚前的灯，
            <br />
            是我路上的光。”<small>诗篇 119:105</small>
          </p>
          {user?.role === "admin" && (
            <button
              className={`admin-nav ${view === "admin" ? "active" : ""}`}
              onClick={() => navigate("admin")}
            >
              <Shield size={16} />
              账号管理
            </button>
          )}
          <button className="profile-chip" onClick={() => navigate("settings")}>
            <Avatar name={user?.nickname || "离"} color={user?.avatar} />
            <span>
              {user?.nickname || "离线阅读"}
              <small>{user ? "愿今天有所领受" : "联网后登录以使用备注"}</small>
            </span>
            <Settings size={16} />
          </button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="icon-btn mobile-menu"
              aria-label="打开导航"
              onClick={() => setSidebar(true)}
            >
              <Menu size={21} />
            </button>
            <span>
              {view === "read"
                ? "阅读空间"
                : view === "notes"
                  ? "我的备注"
                  : view === "friends"
                    ? "与好友同行"
                    : view === "admin"
                      ? "账号管理"
                      : "个人设置"}
            </span>
            {view === "read" && (
              <>
                <ChevronRight size={14} />
                <strong>圣经</strong>
              </>
            )}
          </div>
          <div className="topbar-actions">
            {!online && (
              <span className="offline-badge">
                <WifiOff size={14} />
                离线
              </span>
            )}
            <button
              className="search-trigger"
              onClick={() => setModal("search")}
            >
              <Search size={17} />
              <span>搜索经文</span>
            </button>
            <span className="top-divider" />
            <button
              className="icon-btn"
              aria-label="阅读设置"
              onClick={() => setModal("reading-settings")}
            >
              <Type size={21} />
            </button>
            <button
              className="icon-btn theme-btn"
              aria-label="切换深浅模式"
              onClick={() =>
                setSettings((s) => ({
                  ...s,
                  theme: s.theme === "dark" ? "light" : "dark",
                }))
              }
            >
              {settings.theme === "dark" ? (
                <Sun size={19} />
              ) : (
                <Moon size={19} />
              )}
            </button>
          </div>
        </header>
        {view === "read" ? (
          <div className={`reading-layout ${selected ? "has-selection" : ""}`}>
            <section className="reader" ref={chapterRef}>
              <div className="reader-kicker">
                <span className="eyebrow">THE HOLY BIBLE</span>
                <span className="edition-pill">新标点和合本 · 简体</span>
              </div>
              <div className="chapter-heading">
                <div>
                  <p className="testament-label">
                    {book?.canon === "ot" ? "旧约" : "新约"} · {book?.bookName}
                  </p>
                  <button
                    className="chapter-title"
                    onClick={() => setModal("directory")}
                  >
                    <h1>
                      {book?.bookName || "圣经"}
                      <span>第 {chapterId.split(".")[1]} 章</span>
                    </h1>
                    <ChevronDown size={22} />
                  </button>
                </div>
                <button
                  aria-label="目录"
                  className="directory-btn"
                  onClick={() => setModal("directory")}
                >
                  <BookMarked size={18} />
                  <span>目录</span>
                </button>
              </div>
              <div className="reader-hint">
                <span>
                  <span className="tiny-dot" />
                  安静读，也一起读
                </span>
                <span>
                  <Pencil size={13} />
                  轻点经文，留下感悟
                </span>
              </div>
              {chapterError ? (
                <div className="chapter-error">
                  <Empty icon={WifiOff} title="这一页，等联网再相遇">
                    {chapterError}
                  </Empty>
                  <button
                    className="secondary"
                    onClick={() => setModal("directory")}
                  >
                    选择章节
                  </button>
                </div>
              ) : !chapter ? (
                <div className="reader-loading">
                  <LoaderCircle className="spin" />
                  正在翻页…
                </div>
              ) : (
                <article
                  className="scripture"
                  style={{
                    "--verse-size": `${settings.size}px`,
                    "--verse-line": settings.line,
                  }}
                >
                  {chapter.verses.map((v) => {
                    const count = chapterNotes.filter((n) =>
                      [...v.verseIds, ...v.footnoteVerseIds].includes(
                        n.verse_id,
                      ),
                    ).length;
                    return (
                      <React.Fragment key={v.id}>
                        {chapter.headings
                          .filter((h) => h.beforeVerse === v.number)
                          .map((h, i) => (
                            <h2 className="section-heading" key={i}>
                              {h.text}
                            </h2>
                          ))}
                        {chapter.introductions
                          .filter((h) => h.beforeVerse === v.number)
                          .map((h, i) => (
                            <p className="introduction" key={i}>
                              {h.text}
                            </p>
                          ))}
                        <button
                          id={v.id}
                          aria-label={`第 ${v.label} 节 ${v.text}`}
                          aria-pressed={selected?.id === v.id}
                          className={`verse ${selected?.id === v.id ? "selected" : ""}`}
                          onClick={() => {
                            setEditing(null);
                            setSelected(v);
                            if (user)
                              api("/progress", "PUT", {
                                chapterId,
                                verseId: v.id,
                              }).catch(() => {});
                          }}
                        >
                          <span className="verse-number">{v.label}</span>
                          <span className="verse-text">{v.text}</span>
                          <span
                            className={`verse-note ${count ? "has-notes" : ""}`}
                          >
                            {count ? (
                              <>
                                <MessageCircle size={13} />
                                <small>{count}</small>
                              </>
                            ) : (
                              <Plus size={14} />
                            )}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </article>
              )}
              <div className="chapter-pagination">
                <button
                  className="secondary"
                  disabled={idx <= 0}
                  onClick={() => goTo(chapters[idx - 1])}
                >
                  <ChevronLeft size={17} />
                  上一章
                </button>
                <button
                  className="text-btn"
                  onClick={() => setModal("directory")}
                >
                  {book?.bookName} {chapterId.split(".")[1]} /{" "}
                  {book?.bookChapterMaxNumber}
                </button>
                <button
                  className="primary"
                  disabled={idx < 0 || idx === chapters.length - 1}
                  onClick={() => goTo(chapters[idx + 1])}
                >
                  下一章
                  <ChevronRight size={17} />
                </button>
              </div>
              <p className="copyright">
                新标点和合本 © 1995 香港圣经公会
                <br />
                <a
                  href="/bible/copyright.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  译本版权信息
                </a>
              </p>
            </section>
            <aside className={`notes-rail ${selected ? "open" : ""}`}>
              <header>
                <div>
                  <NotebookPen size={18} />
                  <h2>经文里的回响</h2>
                </div>
                {selected ? (
                  <button
                    className="icon-btn"
                    aria-label="关闭经文备注"
                    onClick={() => {
                      setSelected(null);
                      setEditing(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                ) : (
                  <span className="soft-label">一起读</span>
                )}
              </header>
              {selected ? (
                <>
                  <div className="selected-reference">
                    <span className="eyebrow">此刻读到</span>
                    <h3>
                      {nameOf(selected.id).replace(
                        /:\d+$/,
                        ":" + selected.label,
                      )}
                    </h3>
                    <p>{selected.text}</p>
                    <button
                      className="text-btn"
                      onClick={() =>
                        copy(
                          `${selected.text}\n——${nameOf(selected.id).replace(/:\d+$/, ":" + selected.label)}（新标点和合本）`,
                        )
                      }
                    >
                      <Copy size={13} />
                      复制经文
                    </button>
                  </div>
                  {user ? (
                    <Composer
                      key={`${user.id}-${selected.id}-new`}
                      user={user}
                      verse={selected}
                      editing={null}
                      online={online}
                      onCancel={() => setEditing(null)}
                      onSaved={() => {
                        changed();
                        notify("备注已保存");
                      }}
                      notify={notify}
                    />
                  ) : (
                    <p className="rail-message">
                      联网登录后，可以写备注并与好友分享。
                    </p>
                  )}
                  <div className="rail-notes">
                    <h3>
                      本节备注{" "}
                      <span>
                        {
                          chapterNotes.filter((n) =>
                            [
                              ...selected.verseIds,
                              ...selected.footnoteVerseIds,
                            ].includes(n.verse_id),
                          ).length
                        }
                      </span>
                    </h3>
                    {notesError ? (
                      <p className="muted small-text">
                        {online
                          ? notesError
                          : "离线时暂不显示好友备注，避免展示过期权限的内容。"}
                      </p>
                    ) : chapterNotes.filter((n) =>
                        [
                          ...selected.verseIds,
                          ...selected.footnoteVerseIds,
                        ].includes(n.verse_id),
                      ).length ? (
                      chapterNotes
                        .filter((n) =>
                          [
                            ...selected.verseIds,
                            ...selected.footnoteVerseIds,
                          ].includes(n.verse_id),
                        )
                        .map((n) => (
                          <NoteCard
                            key={n.id}
                            note={n}
                            user={user}
                            onEdit={editNote}
                            onDelete={deleteNote}
                            compact
                          />
                        ))
                    ) : (
                      <p className="muted small-text">
                        还没有备注，你的感悟可以从这里开始。
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Empty title="让阅读，有回响">
                    点选左侧的一节经文，
                    <br />
                    写下感悟，也听见好友的声音。
                  </Empty>
                  <div className="rail-guide">
                    <span className="guide-icon">
                      <Lock size={18} />
                    </span>
                    <div>
                      <h4>把感悟分享给好友</h4>
                      <p>
                        新备注默认好友可见，
                        <br />
                        也可以切换为仅自己可见。
                      </p>
                    </div>
                  </div>
                  <div className="rail-guide">
                    <span className="guide-icon">
                      <Users size={18} />
                    </span>
                    <div>
                      <h4>与朋友，一同领受</h4>
                      <p>
                        成为好友后，
                        <br />
                        在同一节经文下看见彼此的分享。
                      </p>
                    </div>
                  </div>
                  <div className="rail-bottom">
                    <Leaf size={17} />
                    <p>
                      不急着读完，
                      <br />
                      让一句话，在心里停留。
                    </p>
                  </div>
                </>
              )}
            </aside>
          </div>
        ) : view === "notes" ? (
          <NotesPage
            user={user}
            refresh={refresh}
            nameOf={nameOf}
            onGo={goTo}
            onEdit={editNote}
            onDelete={deleteNote}
          />
        ) : view === "friends" ? (
          <FriendsPage
            user={user}
            refresh={refresh}
            nameOf={nameOf}
            onGo={goTo}
            onEdit={editNote}
            onDelete={deleteNote}
            notify={notify}
            ask={ask}
            onChanged={changed}
          />
        ) : view === "admin" ? (
          <AdminPage user={user} notify={notify} ask={ask} copy={copy} />
        ) : (
          <SettingsPage
            user={user}
            setUser={setUser}
            logout={logout}
            settings={settings}
            setSettings={setSettings}
            notify={notify}
            installPrompt={installPrompt}
            onLogin={() => {
              setOfflineGuest(false);
              setUser(null);
            }}
          />
        )}
      </main>
      <nav className="bottom-nav">
        {nav.map(([key, Icon, label]) => (
          <button
            key={key}
            className={view === key ? "active" : ""}
            onClick={() => navigate(key)}
          >
            <Icon size={20} />
            <span>{label === "我的设置" ? "我的" : label}</span>
          </button>
        ))}
      </nav>
      {modal === "edit-note" && editing && (
        <Modal
          title={`编辑备注 · ${nameOf(editing.verse_id)}`}
          onClose={() => {
            setModal(null);
            setEditing(null);
          }}
        >
          <Composer
            key={`edit-${editing.id}`}
            user={user}
            verse={editing.verse}
            editing={editing}
            online={online}
            onCancel={() => {
              setModal(null);
              setEditing(null);
            }}
            onSaved={() => {
              changed();
              setModal(null);
              notify("备注已更新");
            }}
            notify={notify}
          />
        </Modal>
      )}
      {modal === "directory" && (
        <Directory
          books={books}
          current={chapterId}
          onGo={goTo}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "search" && (
        <SearchModal
          books={books}
          online={online}
          user={user}
          nameOf={nameOf}
          onGo={goTo}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "reading-settings" && (
        <Modal title="让阅读更舒服" onClose={() => setModal(null)}>
          <ReadingSettings settings={settings} setSettings={setSettings} />
        </Modal>
      )}
      {confirm && (
        <Modal
          title={confirm.title}
          onClose={() => {
            confirm.resolve(false);
            setConfirm(null);
          }}
        >
          <p className="muted">{confirm.body}</p>
          <div className="modal-actions">
            <button
              className="secondary"
              onClick={() => {
                confirm.resolve(false);
                setConfirm(null);
              }}
            >
              取消
            </button>
            <button
              className="primary"
              onClick={() => {
                confirm.resolve(true);
                setConfirm(null);
              }}
            >
              确认
            </button>
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast" role="status">
          <Check size={17} />
          {toast}
        </div>
      )}
    </div>
  );
}
function Composer({ user, verse, editing, online, onCancel, onSaved, notify }) {
  const key = `rwy-draft-${user.id}-${verse.id}-${editing?.id || "new"}`;
  const initial = storage.get(key, null);
  const [body, setBody] = useState(initial?.body ?? editing?.body ?? ""),
    [visibility, setVisibility] = useState(
      initial?.visibility ?? editing?.visibility ?? "friends",
    ),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const change = (text, vis = visibility) => {
    setBody(text);
    setVisibility(vis);
    try {
      storage.set(key, { body: text, visibility: vis });
    } catch {
      setError("设备空间不足，草稿未能保存，请复制备份");
    }
  };
  return (
    <form
      className="composer"
      onSubmit={async (e) => {
        e.preventDefault();
        setError("");
        if (!online) {
          notify("草稿已保存在当前设备，联网后可提交");
          return;
        }
        setBusy(true);
        try {
          await api(
            editing ? `/notes/${editing.id}` : "/notes",
            editing ? "PATCH" : "POST",
            { verseId: editing?.verse_id || verse.id, body, visibility },
          );
          localStorage.removeItem(key);
          setBody("");
          onSaved();
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="composer-heading">
        <h3>{editing ? "编辑备注" : "写下此刻的感悟"}</h3>
        {editing && (
          <button type="button" className="text-btn" onClick={onCancel}>
            取消编辑
          </button>
        )}
      </div>
      <textarea
        aria-label="经文备注"
        placeholder="这段经文，让你想到了什么？"
        value={body}
        maxLength={5000}
        required
        onChange={(e) => change(e.target.value)}
      />
      <div className="draft-status">
        <span>
          {body ? "草稿已保存在本机" : "你的每一点领受，都值得被记下"}
        </span>
        <span>{body.length}/5000</span>
      </div>
      <div className="composer-bottom">
        <label className="visibility-select">
          {visibility === "private" ? <Lock size={14} /> : <Users size={14} />}
          <select
            aria-label="备注可见范围"
            value={visibility}
            onChange={(e) => change(body, e.target.value)}
          >
            <option value="private">仅自己</option>
            <option value="friends">好友可见</option>
          </select>
        </label>
        <button className="primary small-btn" disabled={busy || !body.trim()}>
          {busy ? "保存中…" : online ? "保存备注" : "保存草稿"}
          <ArrowRight size={14} />
        </button>
      </div>
      {visibility === "friends" && (
        <p className="visibility-help">
          当前及之后添加的好友都可看到这条备注。
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
function NoteCard({
  note: n,
  user,
  onEdit,
  onDelete,
  onGo,
  nameOf,
  compact = false,
}) {
  return (
    <article className={`note-card ${compact ? "compact" : ""}`}>
      <header>
        <Avatar name={n.nickname} color={n.avatar} small />
        <div>
          <strong>
            {n.nickname}
            {n.user_id === user?.id && <span className="me-label">我</span>}
          </strong>
          <small>{fmt(n.created_at)}</small>
        </div>
        <span
          className="note-privacy"
          title={n.visibility === "private" ? "仅自己" : "好友可见"}
        >
          {n.visibility === "private" ? (
            <Lock size={13} />
          ) : (
            <Users size={13} />
          )}
        </span>
      </header>
      {!compact && (
        <button
          className="note-reference"
          onClick={() => onGo(n.verse.chapterId, n.verse_id)}
        >
          <BookOpen size={14} />
          {nameOf(n.verse_id)}
          <ArrowUpRight size={14} />
        </button>
      )}
      <p className="note-body">{n.body}</p>
      {!compact && <blockquote>{n.verse.text}</blockquote>}
      {n.user_id === user?.id && (
        <footer>
          <span>{n.visibility === "private" ? "仅自己可见" : "好友可见"}</span>
          <button className="text-btn" onClick={() => onEdit(n)}>
            <Pencil size={13} />
            编辑
          </button>
          <button className="text-btn" onClick={() => onDelete(n)}>
            <Trash2 size={13} />
            删除
          </button>
        </footer>
      )}
    </article>
  );
}
function Directory({ books, current, onGo, onClose }) {
  const [testament, setTestament] = useState(
      books.find((b) => b.bookUsfm === current.split(".")[0])?.canon || "ot",
    ),
    [chosen, setChosen] = useState(current.split(".")[0]);
  const book = books.find((b) => b.bookUsfm === chosen);
  return (
    <Modal title="翻开圣经" onClose={onClose} wide>
      <div className="directory-tabs segmented">
        <button
          className={testament === "ot" ? "active" : ""}
          onClick={() => {
            setTestament("ot");
            if (book?.canon !== "ot") setChosen("GEN");
          }}
        >
          旧约 · 39 卷
        </button>
        <button
          className={testament === "nt" ? "active" : ""}
          onClick={() => {
            setTestament("nt");
            if (book?.canon !== "nt") setChosen("MAT");
          }}
        >
          新约 · 27 卷
        </button>
      </div>
      <div className="directory-content">
        <div className="books-list">
          {books
            .filter((b) => b.canon === testament)
            .map((b) => (
              <button
                className={b.bookUsfm === chosen ? "active" : ""}
                key={b.bookUsfm}
                onClick={() => setChosen(b.bookUsfm)}
              >
                {b.bookName}
                <span>{b.bookChapterMaxNumber}</span>
              </button>
            ))}
        </div>
        <div className="chapters-picker">
          <h3>
            {book?.bookName}
            <small>选择章节</small>
          </h3>
          <div className="chapters-grid">
            {Array.from({ length: book?.bookChapterMaxNumber || 0 }, (_, i) => (
              <button
                key={i}
                className={`${chosen}.${i + 1}` === current ? "active" : ""}
                onClick={() => onGo(`${chosen}.${i + 1}`)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
function SearchModal({ onClose, onGo, nameOf, user, online }) {
  const [q, setQ] = useState(""),
    [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <Modal title="在经文中寻找" onClose={onClose} wide>
      <form
        className="search-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            if (!user || !online)
              throw Error(
                "经文搜索需要联网登录；离线时可通过目录浏览已下载章节",
              );
            setData(await api(`/search?q=${encodeURIComponent(q.trim())}`));
          } catch (e) {
            setError(e.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Search size={20} />
        <input
          aria-label="搜索经文关键词"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="关键词，或「约翰福音 3:16」"
          maxLength={60}
          required
        />
        <button className="primary small-btn" disabled={busy}>
          {busy ? "搜索中" : "搜索"}
        </button>
      </form>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {data ? (
        <div className="search-results">
          <p className="muted small-text">
            找到 {data.total} 处经文
            {data.total > 80 ? "，显示前 80 条，请缩小关键词范围" : ""}
          </p>
          {data.results.length ? (
            data.results.map((v) => (
              <button
                key={v.id}
                className="search-result"
                onClick={() => onGo(v.chapterId, v.id)}
              >
                <strong>{nameOf(v.id)}</strong>
                <p>{v.text}</p>
                <ArrowUpRight size={15} />
              </button>
            ))
          ) : (
            <Empty icon={Search} title="没有找到相关经文">
              换个词语，或使用卷名、章号和节号试试。
            </Empty>
          )}
        </div>
      ) : (
        <div className="search-suggestions">
          <span className="muted small-text">从一个词开始</span>
          {["爱", "盼望", "平安", "约翰福音 3:16"].map((s) => (
            <button className="chip" onClick={() => setQ(s)} key={s}>
              {s}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
function NotesPage({ user, refresh, nameOf, onGo, onEdit, onDelete }) {
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState("all");
  useEffect(() => {
    if (!user) return;
    setError("");
    api("/notes?scope=mine")
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user?.id, refresh]);
  if (!user)
    return (
      <div className="page-content">
        <Empty icon={WifiOff} title="联网后，再续感悟">
          登录后可以管理自己的经文备注。
        </Empty>
      </div>
    );
  const notes =
    data?.notes.filter((n) => filter === "all" || n.visibility === filter) ||
    [];
  return (
    <div className="page-content">
      <div className="page-heading">
        <div>
          <span className="eyebrow">MY REFLECTIONS</span>
          <h1>把领受，留在这里。</h1>
          <p>每一次记录，都是与经文更深的相遇。</p>
        </div>
        <span className="count-badge">{data?.total ?? "—"} 条备注</span>
      </div>
      <div className="tabs">
        {[
          ["all", "全部备注"],
          ["private", "仅自己"],
          ["friends", "好友可见"],
        ].map(([k, t]) => (
          <button
            key={k}
            className={filter === k ? "active" : ""}
            onClick={() => setFilter(k)}
          >
            {t}
          </button>
        ))}
      </div>
      {error ? (
        <p className="error">{error}</p>
      ) : !data ? (
        <p className="muted">正在整理你的感悟…</p>
      ) : notes.length ? (
        <div className="notes-grid">
          {notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              user={user}
              nameOf={nameOf}
              onGo={onGo}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      ) : (
        <Empty icon={NotebookPen} title="给心里的光，留一个位置">
          {filter === "all"
            ? "阅读时轻点经文，即可写下第一条备注。"
            : "这个分类下还没有备注。"}
        </Empty>
      )}
      {data && data.notes.length < data.total && (
        <button
          className="secondary load-more"
          onClick={async () => {
            try {
              const more = await api(
                `/notes?scope=mine&offset=${data.notes.length}`,
              );
              setData({ ...more, notes: [...data.notes, ...more.notes] });
            } catch (e) {
              setError(e.message);
            }
          }}
        >
          加载更多
        </button>
      )}
    </div>
  );
}
function FriendsPage({
  user,
  refresh,
  nameOf,
  onGo,
  onEdit,
  onDelete,
  notify,
  ask,
  onChanged,
}) {
  const [tab, setTab] = useState("feed"),
    [friends, setFriends] = useState([]),
    [feed, setFeed] = useState(null),
    [username, setUsername] = useState(""),
    [add, setAdd] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [author, setAuthor] = useState("");
  const load = useCallback(async () => {
    if (!user) return;
    setError("");
    try {
      const [f, n] = await Promise.all([
        api("/friends"),
        api(`/notes?scope=feed${author ? "&author=" + author : ""}`),
      ]);
      setFriends(f);
      setFeed(n);
    } catch (e) {
      setFriends([]);
      setFeed(null);
      setError(e.message);
    }
  }, [user?.id, author]);
  useEffect(() => {
    load();
  }, [load, refresh]);
  const action = async (id, method) => {
    try {
      await api(`/friends/${id}`, method);
      onChanged();
    } catch (e) {
      notify(e.message);
    }
  };
  if (!user)
    return (
      <div className="page-content">
        <Empty icon={Users} title="与朋友的相遇，等你上线">
          联网登录后可以添加好友、查看分享。
        </Empty>
      </div>
    );
  return (
    <div className="page-content">
      <div className="page-heading">
        <div>
          <span className="eyebrow">READ TOGETHER</span>
          <h1>同一页，不同的光。</h1>
          <p>读到彼此的感悟，也陪伴彼此的成长。</p>
        </div>
        <button className="primary" onClick={() => setAdd(true)}>
          <UserPlus size={17} />
          添加好友
        </button>
      </div>
      <div className="tabs">
        <button
          className={tab === "feed" ? "active" : ""}
          onClick={() => {
            setTab("feed");
            setAuthor("");
          }}
        >
          我和好友动态
        </button>
        <button
          className={tab === "list" ? "active" : ""}
          onClick={() => setTab("list")}
        >
          我的好友{" "}
          <span>{friends.filter((f) => f.status === "accepted").length}</span>
        </button>
        <button
          className={tab === "requests" ? "active" : ""}
          onClick={() => setTab("requests")}
        >
          好友申请{" "}
          {friends.some((f) => f.status === "pending" && f.incoming) && (
            <span className="notification-dot" />
          )}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {tab === "feed" ? (
        <>
          {author && (
            <button className="chip" onClick={() => setAuthor("")}>
              正在查看一位好友 · 清除筛选
              <X size={13} />
            </button>
          )}
          {feed?.notes.length ? (
            <div className="notes-grid">
              {feed.notes.map((n) => (
                <NoteCard
                  key={n.id}
                  note={n}
                  user={user}
                  onGo={onGo}
                  nameOf={nameOf}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          ) : (
            !error && (
              <Empty icon={Users} title="还没有新的回响">
                你记录的经文感悟和好友分享的感悟会出现在这里。
              </Empty>
            )
          )}
          {feed && feed.notes.length < feed.total && (
            <button
              className="secondary load-more"
              onClick={async () => {
                try {
                  const more = await api(
                    `/notes?scope=feed&offset=${feed.notes.length}${author ? "&author=" + author : ""}`,
                  );
                  setFeed({ ...more, notes: [...feed.notes, ...more.notes] });
                } catch (e) {
                  notify(e.message);
                }
              }}
            >
              加载更多
            </button>
          )}
        </>
      ) : (
        <div className="friend-list">
          {friends
            .filter(
              (f) => f.status === (tab === "list" ? "accepted" : "pending"),
            )
            .map((f) => (
              <div className="friend-row" key={f.id}>
                <Avatar name={f.nickname} color={f.avatar} />
                <div className="friend-info">
                  <strong>{f.nickname}</strong>
                  <small>
                    @{f.username}
                    {f.status === "pending" &&
                      ` · ${f.incoming ? "想与你一起阅读" : "等待对方确认"}`}
                  </small>
                </div>
                {f.status === "accepted" ? (
                  <>
                    <button
                      className="secondary small-btn"
                      onClick={() => {
                        setAuthor(f.userId);
                        setTab("feed");
                      }}
                    >
                      看备注
                    </button>
                    <button
                      className="icon-btn"
                      aria-label={`解除与${f.nickname}的好友关系`}
                      onClick={async () => {
                        if (
                          await ask(
                            "解除好友关系？",
                            "解除后，彼此将无法再查看好友可见的备注。",
                          )
                        )
                          action(f.id, "DELETE");
                      }}
                    >
                      <X size={17} />
                    </button>
                  </>
                ) : (
                  <>
                    {f.incoming && (
                      <button
                        className="primary small-btn"
                        onClick={() => action(f.id, "PATCH")}
                      >
                        接受
                      </button>
                    )}
                    <button
                      className="text-btn"
                      onClick={() => action(f.id, "DELETE")}
                    >
                      {f.incoming ? "拒绝" : "撤回"}
                    </button>
                  </>
                )}
              </div>
            ))}
          {!friends.some(
            (f) => f.status === (tab === "list" ? "accepted" : "pending"),
          ) &&
            !error && (
              <Empty
                icon={tab === "list" ? Users : CheckCheck}
                title={
                  tab === "list" ? "邀请一个朋友，一起开始" : "没有待处理的申请"
                }
              >
                {tab === "list"
                  ? "通过完整用户名添加好友，双方确认后开始分享。"
                  : "新的好友申请会显示在这里。"}
              </Empty>
            )}
        </div>
      )}
      {add && (
        <Modal title="邀请朋友，一起阅读" onClose={() => setAdd(false)}>
          <p className="muted">
            输入朋友的完整用户名。对方接受后，即可互相查看分享的备注。
          </p>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await api("/friends", "POST", { username });
                setAdd(false);
                setUsername("");
                setTab("requests");
                onChanged();
                notify("好友申请已发送");
              } catch (e) {
                notify(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              好友用户名
              <input
                autoCapitalize="none"
                autoComplete="off"
                spellCheck="false"
                placeholder="例如 reader_01"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </label>
            <p className="small-text muted">你的用户名：{user.username}</p>
            <button className="primary" disabled={busy}>
              {busy ? "发送中…" : "发送申请"}
              <ArrowRight size={17} />
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
function ReadingSettings({ settings, setSettings }) {
  return (
    <div className="reading-settings stack">
      <label>
        字体大小 <span>{settings.size}px</span>
        <input
          aria-label="字体大小"
          type="range"
          min="16"
          max="32"
          step="1"
          value={settings.size}
          onChange={(e) =>
            setSettings({ ...settings, size: Number(e.target.value) })
          }
        />
      </label>
      <label>
        行间距 <span>{settings.line.toFixed(1)}</span>
        <input
          aria-label="行间距"
          type="range"
          min="1.6"
          max="2.6"
          step="0.1"
          value={settings.line}
          onChange={(e) =>
            setSettings({ ...settings, line: Number(e.target.value) })
          }
        />
      </label>
      <div className="segmented">
        <button
          className={settings.theme === "light" ? "active" : ""}
          onClick={() => setSettings({ ...settings, theme: "light" })}
        >
          <Sun size={16} />
          日间
        </button>
        <button
          className={settings.theme === "dark" ? "active" : ""}
          onClick={() => setSettings({ ...settings, theme: "dark" })}
        >
          <Moon size={16} />
          夜间
        </button>
      </div>
      <p
        className="settings-preview"
        style={{ fontSize: settings.size, lineHeight: settings.line }}
      >
        你的话是我脚前的灯，
        <br />
        是我路上的光。
      </p>
    </div>
  );
}
function SettingsPage({
  user,
  setUser,
  logout,
  settings,
  setSettings,
  notify,
  installPrompt,
  onLogin,
}) {
  const [nickname, setNickname] = useState(user?.nickname || ""),
    [avatar, setAvatar] = useState(user?.avatar || "sage"),
    [pw, setPw] = useState(false),
    [download, setDownload] = useState(0),
    [downloaded, setDownloaded] = useState(false),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!("caches" in window)) return;
    caches
      .open("rwy-scripture-v1")
      .then((c) => c.keys())
      .then((k) =>
        setDownloaded(
          k.filter((r) => r.url.includes("/chapters/")).length >= 1189,
        ),
      )
      .catch(() => {});
  }, []);
  const offline = async () => {
    if (!("caches" in window))
      return notify("离线下载需要 HTTPS 或 localhost 环境");
    setDownload(1);
    try {
      const r = await fetch("/bible/offline.json");
      if (!r.ok) throw Error("下载失败");
      const all = await r.json(),
        cache = await caches.open("rwy-scripture-v1");
      for (let i = 0; i < all.length; i++) {
        await cache.put(
          `/bible/chapters/${all[i].id}.json`,
          new Response(JSON.stringify(all[i]), {
            headers: { "Content-Type": "application/json" },
          }),
        );
        if (i % 30 === 0)
          setDownload(Math.max(1, Math.round(((i + 1) / all.length) * 100)));
      }
      setDownloaded(true);
      notify("全本经文已下载，可以安心离线阅读");
    } catch (e) {
      notify("下载未完成，请检查网络或设备空间，稍后可重试");
    } finally {
      setDownload(0);
    }
  };
  return (
    <div className="page-content settings-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">YOUR READING SPACE</span>
          <h1>以舒服的方式，阅读。</h1>
          <p>把这里，变成属于你的阅读空间。</p>
        </div>
      </div>
      <div className="settings-grid">
        <section className="settings-card">
          <h2>个人资料</h2>
          {user ? (
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                try {
                  setUser(await api("/profile", "PATCH", { nickname, avatar }));
                  notify("个人资料已保存");
                } catch (e) {
                  notify(e.message);
                } finally {
                  setSaving(false);
                }
              }}
            >
              <div className="profile-preview">
                <Avatar name={nickname || user.username} color={avatar} />
                <div>
                  <strong>{user.nickname}</strong>
                  <small>@{user.username}</small>
                </div>
              </div>
              <label>
                昵称
                <input
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  maxLength={24}
                  required
                />
              </label>
              <label>头像颜色</label>
              <div className="color-options">
                {COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`color-choice ${c} ${avatar === c ? "selected" : ""}`}
                    aria-label={`头像颜色 ${c}`}
                    onClick={() => setAvatar(c)}
                  >
                    {avatar === c && <Check size={18} />}
                  </button>
                ))}
              </div>
              <button className="primary" disabled={saving}>
                {saving ? "保存中…" : "保存资料"}
              </button>
              <button
                type="button"
                className="text-btn"
                onClick={() => setPw(true)}
              >
                <Lock size={14} />
                修改密码
              </button>
            </form>
          ) : (
            <>
              <p className="muted">联网登录后，管理个人资料与阅读备注。</p>
              <button className="primary" onClick={onLogin}>
                返回登录
              </button>
            </>
          )}
        </section>
        <section className="settings-card">
          <h2>阅读偏好</h2>
          <ReadingSettings settings={settings} setSettings={setSettings} />
        </section>
        <section className="settings-card">
          <h2>
            <Download size={19} />
            随时随地，继续阅读
          </h2>
          <p className="muted">
            下载全本经文后，没有网络也能翻阅 66
            卷书。备注草稿保存在当前设备，联网后手动提交。
          </p>
          <button
            className="secondary full"
            disabled={download > 0 || downloaded}
            onClick={offline}
          >
            {download ? (
              <>
                <LoaderCircle className="spin" size={16} />
                正在保存 {download}%
              </>
            ) : downloaded ? (
              <>
                <CheckCheck size={17} />
                全本经文已下载
              </>
            ) : (
              <>
                <Download size={17} />
                下载全本经文
              </>
            )}
          </button>
          <div className="install-section">
            <h3>把阅读放到主屏幕</h3>
            {installPrompt ? (
              <button
                className="primary"
                onClick={async () => {
                  await installPrompt.prompt();
                }}
              >
                安装应用
                <ArrowUpRight size={16} />
              </button>
            ) : (
              <p className="muted small-text">
                iPhone / iPad：在 Safari 分享菜单中选择「添加到主屏幕」。Android
                / 电脑：在浏览器菜单中选择「安装应用」。
              </p>
            )}
          </div>
        </section>
        <section className="settings-card about-card">
          <div className="brand-mark">
            <BookOpen size={28} />
          </div>
          <h2>和你一起阅读</h2>
          <p className="muted">阅读 · 记录 · 同行</p>
          <p className="small-text muted">
            圣经新标点和合本（简体）
            <br />
            Copyright © 1995 Hong Kong Bible Society.
            <br />
            Used by permission.
          </p>
          <a
            className="text-btn"
            href="/bible/copyright.html"
            target="_blank"
            rel="noreferrer"
          >
            查看译本版权信息
            <ArrowUpRight size={13} />
          </a>
          <span className="version-label">VERSION 0.1.0</span>
        </section>
      </div>
      {user && (
        <button className="logout-btn" onClick={logout}>
          <LogOut size={17} />
          退出登录
        </button>
      )}
      {pw && (
        <Modal title="修改登录密码" onClose={() => setPw(false)}>
          <PasswordForm
            onDone={(u) => {
              setUser(u);
              setPw(false);
              notify("密码已更新，其他设备已退出登录");
            }}
          />
        </Modal>
      )}
    </div>
  );
}
function AdminPage({ user, notify, ask, copy }) {
  const [users, setUsers] = useState([]),
    [error, setError] = useState(""),
    [create, setCreate] = useState(false),
    [username, setUsername] = useState(""),
    [credentials, setCredentials] = useState(null),
    [busy, setBusy] = useState(false);
  const load = () =>
    api("/admin/users")
      .then(setUsers)
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  if (user?.role !== "admin")
    return <Empty icon={Shield} title="需要管理员权限" />;
  return (
    <div className="page-content">
      <div className="page-heading">
        <div>
          <span className="eyebrow">ADMINISTRATION</span>
          <h1>让更多人，一起阅读。</h1>
          <p>创建账号，将初始登录信息交给你的读者。</p>
        </div>
        <button className="primary" onClick={() => setCreate(true)}>
          <Plus size={18} />
          创建账号
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>读者</th>
              <th>用户名</th>
              <th>状态</th>
              <th>创建日期</th>
              <th>管理</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <strong>{u.nickname}</strong>
                  {u.role === "admin" && (
                    <small className="role-badge">管理员</small>
                  )}
                </td>
                <td className="mono">{u.username}</td>
                <td>
                  <span className={`status-pill ${u.active ? "" : "disabled"}`}>
                    {u.active
                      ? u.must_change
                        ? "待首次登录"
                        : "正常"
                      : "已停用"}
                  </span>
                </td>
                <td>{u.created_at.slice(0, 10)}</td>
                <td>
                  {u.id !== user.id ? (
                    <div className="table-actions">
                      <button
                        className="text-btn"
                        onClick={async () => {
                          if (
                            !(await ask(
                              "重置这个账号的密码？",
                              "该用户在其他设备上的登录会失效，下次登录需修改初始密码。",
                            ))
                          )
                            return;
                          try {
                            setCredentials(
                              await api(`/admin/users/${u.id}/reset`, "POST"),
                            );
                            load();
                          } catch (e) {
                            notify(e.message);
                          }
                        }}
                      >
                        重置密码
                      </button>
                      <button
                        className="text-btn"
                        onClick={async () => {
                          if (
                            !(await ask(
                              u.active ? "停用这个账号？" : "恢复这个账号？",
                              u.active
                                ? "该账号将无法登录，已有登录会话会立即失效。"
                                : "恢复后，读者可以重新登录。",
                            ))
                          )
                            return;
                          try {
                            await api(`/admin/users/${u.id}`, "PATCH", {
                              active: !u.active,
                            });
                            load();
                          } catch (e) {
                            notify(e.message);
                          }
                        }}
                      >
                        {u.active ? "停用" : "启用"}
                      </button>
                    </div>
                  ) : (
                    <span className="muted small-text">当前账号</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {create && (
        <Modal title="创建一个阅读账号" onClose={() => setCreate(false)}>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                setCredentials(await api("/admin/users", "POST", { username }));
                setCreate(false);
                setUsername("");
                load();
              } catch (e) {
                notify(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              用户名 <span className="muted">（选填）</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                pattern="[a-zA-Z0-9_]{3,32}"
                placeholder="留空可自动生成"
                autoCapitalize="none"
                autoComplete="off"
              />
            </label>
            <p className="muted small-text">
              3–32
              位字母、数字或下划线。初始密码由系统随机生成，首次登录时读者需要修改。
            </p>
            <button className="primary" disabled={busy}>
              {busy ? "创建中…" : "创建并生成密码"}
              <ArrowRight size={17} />
            </button>
          </form>
        </Modal>
      )}
      {credentials && (
        <Modal title="账号已准备好" onClose={() => setCredentials(null)}>
          <p className="muted">初始密码仅在这里展示一次，请复制后交给读者。</p>
          <div className="credentials">
            <label>
              用户名<strong>{credentials.username}</strong>
            </label>
            <label>
              初始密码<strong>{credentials.password}</strong>
            </label>
          </div>
          <button
            className="primary full"
            onClick={() =>
              copy(
                `和你一起阅读\n用户名：${credentials.username}\n初始密码：${credentials.password}\n首次登录请修改密码。`,
              )
            }
          >
            <Copy size={16} />
            复制账号信息
          </button>
        </Modal>
      )}
    </div>
  );
}

createRoot(document.getElementById("root")).render(<App />);
